import React, { PureComponent } from 'react';
import {
  node, func,
  object, string, bool,
} from 'prop-types';
import isEmpty from 'lodash/isEmpty';
import isEqualWith from 'lodash/isEqualWith';
import noop from 'lodash/noop';
import Grid from '@active/react-ui/Grid';
import GridItem from '@active/react-ui/GridItem';
import Button from '@active/react-ui/Button';
import Form, { FormConsumer } from '@active/react-ui/Form';
import AddressSuggestion from '@active/react-address-editor/components/AddressSuggestion';
import Locale from '@active/react-utils/i18n/Locale';
import { withI18n } from '@active/react-utils/i18n/LocaleContext';
import addressFormatter from '../../../../utils/addressFormatter';
import ErrorMsg from './components/ErrorMsg';
import { handleKeyboardEvent, autoScrollToTheErrorField } from '../../../../../../../../../shared/utils/commonUtils';
import pubSubInstance from '../../../../../../../../../shared/utils/pubSub';

class BillingContactForm extends PureComponent {
  static propTypes = {
    component: node.isRequired,
    onContactChange: func.isRequired,
    verifyAVS: func,
    i18n: func.isRequired,
    billingContact: object,
    widgetNameSpace: string.isRequired,
    isSupportAVS: bool,
    trackingCallback: func,
    logTrack: func,
  };

  static defaultProps = {
    isSupportAVS: false,
    verifyAVS: noop,
    trackingCallback: noop,
    logTrack: noop,
  };

  constructor(props) {
    super(props);

    this.state = {
      isStatic: false,
      addrSuggestions: [],
      errorInfo: {},
      isShowCancelBtn: false,
      defaultAddress: {},
      preBillingContact: {},
      isValidAVS: false,
      savedFormAdress: this.props.billingContact,
    };
  }

  handleValidate = () => {
    const { isSupportAVS } = this.props;
    const { isValidAVS } = this.state;
    return new Promise((resolve) => {
      this.form.validateForm({ shouldTouchFields: true }).then((res) => {
        const formValid = isEmpty(res);
        if (formValid) {
          if (isSupportAVS) {
            resolve(isValidAVS);
            if (!isValidAVS) {
              this.showCancelOrConfirmError();
            }
          }
          resolve(true);
        }
        resolve(false);
      });
    });
  };

  handleSubmit = () => {
    const { values } = this.form;
    return new Promise((resolve) => {
      const billingContact = {
        ...values,
        address: addressFormatter(values.address),
      };
      resolve({ billingContact });
    });
  };

  componentDidMount() {
    const { widgetNameSpace } = this.props;
    pubSubInstance.subscribe(`${widgetNameSpace}-payment-item-validate`, this.handleValidate);
    pubSubInstance.subscribe(`${widgetNameSpace}-payment-item-submit`, this.handleSubmit);
  }

  componentWillUnmount() {
    const { widgetNameSpace } = this.props;
    pubSubInstance.unsubscribe(`${widgetNameSpace}-payment-item-validate`, this.handleValidate);
    pubSubInstance.unsubscribe(`${widgetNameSpace}-payment-item-submit`, this.handleSubmit);
  }

  verifyAVS = (autoScroll = false) => {
    const container = document.getElementsByClassName('avs-billing-contact')[0];
    const { verifyAVS, logTrack } = this.props;
    const { address, address: { country } = {} } = this.form && this.form.values;
    this.form.validateForm({ shouldTouchFields: true }).then(
      (errors) => {
        if (isEmpty(errors)) {
          if (country === 'US') {
            verifyAVS({ params: { address } }).then(({ data }) => {
              if (data) {
                const { passVerification, suggestedAddresses, errorDescription } = data;
                if (passVerification) {
                  this.setState({
                    isValidAVS: true,
                    isStatic: true,
                    errorInfo: {},
                  });
                  this.form.setValues(
                    {
                      ...this.form.values,
                      address:
                      {
                        line2: '',
                        ...suggestedAddresses[0],
                      },
                    },
                    true,
                  );
                } else {
                  this.setState({
                    isValidAVS: false,
                    isStatic: false,
                    addrSuggestions: suggestedAddresses,
                    errorInfo: {
                      type: 'danger',
                      message: errorDescription, // ADDRESS_ERROR
                      suggestedAddresses,
                      messageId: suggestedAddresses[0] ? 'checkout.lib.error.theAddressNotSeemRight' : undefined, // ADDRESS_SUGGEST
                    },
                  });
                  autoScroll && autoScrollToTheErrorField(container); // Just autoScroll after click confirm btn
                  logTrack?.({
                    params: {
                      message: `verifyAVS failed: ${errorDescription}`,
                    },
                  });
                }
              } else {
                this.setState({
                  isValidAVS: false,
                  isStatic: false,
                  errorInfo: {},
                });
              }
            });
          } else {
            this.setState({
              isValidAVS: true,
              isStatic: true,
              errorInfo: {},
            });
          }
        } else {
          this.setState({ isValidAVS: false });
          autoScroll && autoScrollToTheErrorField(container); // Click confirm btn and verify fail
        }
      });
  }

  makeChange = () => {
    const { trackingCallback } = this.props;
    trackingCallback({ id: 'cui.makeChange.clicked' });
    this.setState({
      isValidAVS: false,
      isStatic: false,
      errorInfo: {},
      isShowCancelBtn: true,
      preBillingContact: this.form.values,
    });
  }

  handleCancel= () => {
    const { preBillingContact } = this.state;

    this.setState({
      isValidAVS: true,
      isStatic: true,
      errorInfo: {},
      addrSuggestions: [],
    });
    this.form.setValues(preBillingContact, true);
  }

  handleConfirm = () => {
    this.setState({ addrSuggestions: [] }, () => {
      this.verifyAVS(true);
    });
  }

  handleUseSuggestion = () => {
    const { addrSuggestions } = this.state;
    this.form.setValues({
      ...this.form.values,
      address: {
        line2: '', // line2 maybe not in response, so should clear line2 input box
        ...addrSuggestions[0],
      },
    }, true);
    this.setState({
      isValidAVS: true,
      addrSuggestions: [],
      isStatic: true,
      errorInfo: {},
    });
  }

  handleUseAddress = () => {
    this.setState({
      isValidAVS: true,
      addrSuggestions: [],
      isStatic: true,
      errorInfo: {},
    });
    this.form.setValues(this.form.values, true);
  }

  showCancelOrConfirmError = () => { // Show error when submit but AVS not confirm or cancel
    this.setState({
      errorInfo: { type: 'danger', messageId: 'checkout.lib.components.error.cancelOrConfirmBeforeProceeding' },
    });
    const container = document.getElementsByClassName('avs-billing-contact')[0];
    autoScrollToTheErrorField(container);
  }

  renderReadyCallback = () => {
    this.verifyAVS();
  }

  handleContactChange = ({ values }) => {
    const { isSupportAVS, onContactChange } = this.props;
    onContactChange(values);
    if (!isSupportAVS) {
      return;
    }
    const { savedFormAdress } = this.state;
    if (!isEqualWith(savedFormAdress, values)) {
      this.setState({
        errorInfo: {},
        savedFormAdress: values,
      });
    }
  };

  render() {
    const {
      i18n, component: ContactComponent, isSupportAVS, billingContact,
    } = this.props;
    const {
      isStatic, addrSuggestions, errorInfo, isShowCancelBtn,
    } = this.state;

    return (<Form
      customizeLayout
      id="bill-address-static"
      initialValues={billingContact}
      enableReinitialize
      className="payment-method-fields__billingContact cui-mg-t-20"
      onChange={this.handleContactChange}>
      <Grid container={false} rowGap={0} colGap={0} >
        <GridItem span={12} spanXl={8}>
          {
            isSupportAVS
              ? <div className="avs-billing-contact" >
                {!isEmpty(errorInfo)
                && <ErrorMsg {...errorInfo} useAddress={this.handleUseAddress}/>
                }
                <ContactComponent static={isStatic} renderReadyCallback={this.renderReadyCallback}/>
                {isStatic && <div
                  aria-labelledby="make-change-btn-label"
                  aria-describedby="bill-address-static"
                  className="avs-billing-contact__make-change"
                  role="button" tabIndex={0}
                  onKeyDown={handleKeyboardEvent(this.makeChange, 'Enter')}
                  onClick={this.makeChange}>
                  <span id="make-change-btn-label" aria-hidden>
                    <Locale id="checkout.lib.components.common.text.makeChange" />
                  </span>
                </div>}
                {
                  !isStatic
                  && <div className="avs-billing-contact__btns">
                    <Button noSubmit onClick={this.handleConfirm} className="avs-billing-contact__btn-confirm">
                      <Locale id="checkout.lib.components.common.btn.confirm" />
                    </Button>
                    {
                      isShowCancelBtn && <Button noSubmit onClick={this.handleCancel} type="secondary" className="avs-billing-contact__btn-cancel">
                        <Locale id="checkout.lib.components.common.btn.cancel" />
                      </Button>
                    }
                  </div>
                }
                {
                  !!addrSuggestions[0]
                  && <AddressSuggestion
                    title={i18n('checkout.lib.components.text.suggestedAddress')}
                    useSuggestionLinkText={i18n('checkout.lib.components.text.useThisAddress')}
                    source={this.form.values.address}
                    suggestion={addrSuggestions[0]}
                    onUseSuggestion={this.handleUseSuggestion} />
                }
              </div> : <ContactComponent />
          }
        </GridItem>
      </Grid>
      <FormConsumer>
        {
          ({
            form,
          }) => {
            this.form = form;
          }
        }
      </FormConsumer>
    </Form>
    );
  }
}

export default withI18n(BillingContactForm);
